import { useState } from "react";
import {
  BUYER_SEQUENCE_SIGNATURE_KEY,
  buyerSequenceTemplateKey,
  type BuyerSequenceBlockKey,
  type BuyerSequenceSelection,
  type BuyerSequenceStepKey,
} from "@/api/buyerSequence";
import { Button } from "@/design-system/components/ui";
import { CallHoursCard } from "./CallHoursCard";
import { FieldsReference } from "./FieldsReference";
import { useWording, useWordingFields } from "./useWording";
import { WordingSwitch } from "./WordingSwitch";
import { WordingTemplateEditor, type WordingMode } from "./WordingTemplateEditor";

/**
 * The Outreach tab's Edit wording mode (L12). Mounted by `OutreachTab` in place
 * of the send-mode body; DONE returns to send mode.
 *
 * Two switches and nothing else choose what is edited: WHOSE wording (the team
 * default, or the user's own version — "My version" first, because that is the
 * one anybody may change) and WHICH message (the text, or the email with its
 * signature). There is no timing and no stop rule here: the steps are the
 * steps, and only their words are editable.
 *
 * Both templates stay mounted for the life of the panel, so switching between
 * Text and Email never throws away half-typed wording.
 */
export interface EditWordingPanelProps {
  buyerId: string;
  /** The step selected in send mode, opened first. */
  stepKey: BuyerSequenceStepKey;
  /** The "Message about" selection, so the live preview renders against it. */
  selection: BuyerSequenceSelection;
  onDone: () => void;
}

type WordingView = "text" | "email";

const MODE_OPTIONS = [
  { value: "team", label: "Team default" },
  { value: "mine", label: "My version" },
] as const;

const VIEW_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
] as const;

const TEXT_BLOCKS: readonly BuyerSequenceBlockKey[] = ["text"];
const EMAIL_BLOCKS: readonly BuyerSequenceBlockKey[] = ["subject", "email"];
const SIGNATURE_BLOCKS: readonly BuyerSequenceBlockKey[] = ["signature"];

export function EditWordingPanel({ buyerId, stepKey, selection, onDone }: EditWordingPanelProps) {
  const [mode, setMode] = useState<WordingMode>("mine");
  const [view, setView] = useState<WordingView>("text");

  const templateKey = buyerSequenceTemplateKey(stepKey);
  const step = useWording(templateKey);
  const fields = useWordingFields();
  // Until the wording arrives the heading names no step: the raw key
  // ("follow-up-1") is not a name anybody reads.
  const stepName = step.data?.name;

  const teamMode = mode === "team";

  return (
    <div className="flex flex-col gap-space-4" data-testid="outreach-edit-wording">
      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="flex min-w-0 flex-col gap-space-1">
          <h3 className="font-display text-lead font-bold text-onyx" data-testid="wording-heading">
            {stepName ? `Edit wording — ${stepName}` : "Edit wording"}
          </h3>
          <p className="text-small text-gray-mid">
            {teamMode
              ? "The team default is what everyone uses unless they have made their own version."
              : "Your version is used only in the messages you send. The team default does not change."}
          </p>
        </div>
        <Button onClick={onDone}>Done</Button>
      </div>

      <div className="flex flex-wrap items-center gap-space-3">
        <WordingSwitch
          label="Whose wording"
          value={mode}
          options={MODE_OPTIONS}
          onChange={setMode}
          data-testid="wording-mode"
        />
        <WordingSwitch
          label="Which message"
          value={view}
          options={VIEW_OPTIONS}
          onChange={setView}
          data-testid="wording-view"
        />
      </div>

      {/* Keyed on the template: a different step is a different working copy. */}
      <WordingTemplateEditor
        key={templateKey}
        buyerId={buyerId}
        templateKey={templateKey}
        mode={mode}
        visibleBlocks={view === "text" ? TEXT_BLOCKS : EMAIL_BLOCKS}
        selection={selection}
        fields={fields.data}
        heading={view === "text" ? "Text message" : "Email"}
        description={
          view === "text"
            ? "The text message for this step."
            : "The email subject and body for this step. The signature below is added after the body."
        }
      />

      <WordingTemplateEditor
        buyerId={buyerId}
        templateKey={BUYER_SEQUENCE_SIGNATURE_KEY}
        mode={mode}
        visibleBlocks={SIGNATURE_BLOCKS}
        selection={selection}
        fields={fields.data}
        heading={teamMode ? "Team email signature" : "My email signature"}
        description="Added below the email body in every step."
        isSignature
        hidden={view !== "email"}
      />

      <CallHoursCard />
      <FieldsReference />
    </div>
  );
}
